import { z, ZodSchema, ZodType, ZodTypeDef } from 'zod'
import { isNumber } from 'lodash'

import { isBmiWithinRange } from '@gaia-family/lib'

import {
  AmhSchema,
  WeightSchema,
  HeightSchema,
  Age18,
  NonNegativeIntSchema,
} from '../shared/schema'

import { defaultPreviousIvfFields } from './treatmentDefaultValues'
import { TreatmentPathwayWithExplicitProcedures } from './pathway'

export type PredictionError = {
  readonly code: string
  readonly message: string
}

export const IndividualRelationshipEnum = {
  Self: 'SELF',
  Partner: 'PARTNER',
  Donor: 'DONOR',
  Surrogate: 'SURROGATE',
  None: 'NONE',
  Unknown: 'UNKNOWN',
  Unsure: 'UNSURE',
  Other: 'OTHER',
} as const

export const IndividualSexEnum = {
  Male: 'MALE',
  Female: 'FEMALE',
  Unknown: 'UNKNOWN',
} as const

export const TreatmentProfileStatusEnum = {
  NotStarted: 'NOT_STARTED',
  PartiallyComplete: 'PARTIALLY_COMPLETE',
  Complete: 'COMPLETE',
} as const

export const PathwayPredictionStatusEnum = {
  Unsupported: 'UNSUPPORTED',
  Predicted: 'PREDICTED',
  Overriden: 'OVERRIDDEN',
} as const

export const FertilityDiagnosisEnum = {
  TubalDisease: 'TUBAL_DISEASE',
  OvulatoryDisease: 'OVULATORY_DISEASE',
  Pcos: 'PCOS',
  Dor: 'DOR',
  Uterine: 'UTERINE',
  Endometriosis: 'ENDOMETRIOSIS',
  Unexplained: 'UNEXPLAINED',
  MaleFactor: 'MALE_FACTOR',
  None: 'NONE',
  DontKnow: 'DONT_KNOW',
  Other: 'OTHER',
} as const

export const UserResponseEnum = {
  Yes: 'YES',
  No: 'NO',
  NotSure: 'NOT_SURE',
} as const

export const AttemptedConceptionPeriodEnum = {
  LessThan_12Months: 'LESS_THAN_12_MONTHS',
  Between_12_24Months: 'BETWEEN_12_24_MONTHS',
  MoreThan_24Months: 'MORE_THAN_24_MONTHS',
  Unknown: 'UNKNOWN',
} as const

export const EmbryoTransferTypeEnum = {
  Fresh: 'FRESH',
  Frozen: 'FROZEN',
} as const

export const IntendedStartPeriodEnum = {
  NextThreeMonths: 'NEXT_THREE_MONTHS',
  NextSixMonths: 'NEXT_SIX_MONTHS',
  NextTwelveMonths: 'NEXT_TWELVE_MONTHS',
  DontKnow: 'DONT_KNOW',
} as const

/**
 * Types of consultations users may have already had
 */
export const ConsultationTypeEnum = {
  Gp: 'GP',
  NhsHospital: 'NHS_HOSPITAL',
  PrivateFertilityClinic: 'PRIVATE_FERTILITY_CLINIC',
  Obgyn: 'OBGYN',
  None: 'NONE',
  Other: 'OTHER',
} as const

export const NhsFundingEligibilityEnum = {
  CompletedNhs: 'COMPLETED_NHS',
  Ineligible: 'INELIGIBLE',
  DontKnow: 'DONT_KNOW',
  EligibleConsideringPrivate: 'ELIGIBLE_CONSIDERING_PRIVATE',
  EligiblePresentlyNhs: 'ELIGIBLE_PRESENTLY_NHS',
  Other: 'OTHER',
} as const

export const IvfUsProcedureEnum = {
  PgtM: 'PGT_M',
  PgtA: 'PGT_A',
} as const

const IvfBaseProceduresEnum = {
  Icsi: 'ICSI',
  EggDonation: 'EGG_DONATION',
  SpermDonation: 'SPERM_DONATION',
  Surrogate: 'SURROGATE',
  Pgd: 'PGD',
  FreezeAll: 'FREEZE_ALL',
  SharedMotherhood: 'SHARED_MOTHERHOOD',
  None: 'NONE',
  SingleVialDonorSperm: 'SINGLE_VIAL_DONOR_SPERM',
  Ssr: 'SSR',
  PgtSr: 'PGT_SR',
  MildIvf: 'MILD_IVF',
  DuoStimulation: 'DUO_STIMULATION',
  EmbryoScope: 'EMBRYO_SCOPE',
  EmbryoGlue: 'EMBRYO_GLUE',
  Imsi: 'IMSI',
  Picsi: 'PICSI',
  TimelapseImaging: 'TIMELAPSE_IMAGING',
  IntrauterineCulture: 'INTRAUTERINE_CULTURE',
  ImmunologicalTests: 'IMMUNOLOGICAL_TESTS',
  ViralScreening: 'VIRAL_SCREENING',
  EndometrialScratching: 'ENDOMETRIAL_SCRATCHING',
  Era: 'ERA',
  AssistedHatching: 'ASSISTED_HATCHING',
  ArtificialEggActivationCalciumIonophore:
    'ARTIFICIAL_EGG_ACTIVATION_CALCIUM_IONOPHORE',
  Fbc: 'FBC',
  Geri: 'GERI',
  ThyroidTests: 'THYROID_TESTS',
  Laperoscopy: 'LAPEROSCOPY',
  Hysteroscopy: 'HYSTEROSCOPY',
  Intralipids: 'INTRALIPIDS',
  AdditionalScans: 'ADDITIONAL_SCANS',
  EmbryoDonation: 'EMBRYO_DONATION',
  Medication: 'MEDICATION',
} as const

export const IvfProcedureEnum = {
  ...IvfBaseProceduresEnum,
  ...IvfUsProcedureEnum,
}

export const ExplicitIvfProcedureEnum = {
  Icsi: 'ICSI',
  Pgd: 'PGD',
  FreezeAll: 'FREEZE_ALL',
  None: 'NONE',
  SingleVialDonorSperm: 'SINGLE_VIAL_DONOR_SPERM',
  Ssr: 'SSR',
  PgtSr: 'PGT_SR',
  MildIvf: 'MILD_IVF',
  DuoStimulation: 'DUO_STIMULATION',
  EmbryoScope: 'EMBRYO_SCOPE',
  EmbryoGlue: 'EMBRYO_GLUE',
  Imsi: 'IMSI',
  Picsi: 'PICSI',
  TimelapseImaging: 'TIMELAPSE_IMAGING',
  IntrauterineCulture: 'INTRAUTERINE_CULTURE',
  ImmunologicalTests: 'IMMUNOLOGICAL_TESTS',
  ViralScreening: 'VIRAL_SCREENING',
  EndometrialScratching: 'ENDOMETRIAL_SCRATCHING',
  Era: 'ERA',
  AssistedHatching: 'ASSISTED_HATCHING',
  ArtificialEggActivationCalciumIonophore:
    'ARTIFICIAL_EGG_ACTIVATION_CALCIUM_IONOPHORE',
  Fbc: 'FBC',
  Geri: 'GERI',
  ThyroidTests: 'THYROID_TESTS',
  Laperoscopy: 'LAPEROSCOPY',
  Hysteroscopy: 'HYSTEROSCOPY',
  Intralipids: 'INTRALIPIDS',
  AdditionalScans: 'ADDITIONAL_SCANS',
  EmbryoDonation: 'EMBRYO_DONATION',
  Medication: 'MEDICATION',
  ...IvfUsProcedureEnum,
} as const

export const FertilityProfileFlagEnum = {
  BMI_WARN: 'BMI_WARN',
  BMI_GT_35: 'BMI_GT_35',
  BMI_LT_10: 'BMI_LT_10',
  INVALID_BMI: 'INVALID_BMI',
  PREDICTION_LT_55: 'PREDICTION_LT_55',
  NO_PREDICTIONS: 'NO_PREDICTIONS',
  MALE_FACTOR_NO_ICSI: 'MALE_FACTOR_NO_ICSI',
  PREVIOUS_ROUNDS_GT_0: 'PREVIOUS_ROUNDS_GT_0',
  PREVIOUS_LB_GT_0: 'PREVIOUS_LB_GT_0',
  SHARED_MOTHERHOOD: 'SHARED_MOTHERHOOD',
  INPUT_FREE_TEXT: 'INPUT_FREE_TEXT',
  EXISTING_DIAGNOSIS: 'EXISTING_DIAGNOSIS',
  EXISTING_PROCEDURES: 'EXISTING_PROCEDURES',
  AGE_LT_38: 'AGE_LT_38',
  PROCEDURE_NOT_INCLUDED_IN_PRICING: 'PROCEDURE_NOT_INCLUDED_IN_PRICING',
  UNSUPPORTED_PROCEDURES: 'UNSUPPORTED_PROCEDURES',
  SINGLE: 'SINGLE',
  EGG_PROVIDER_OVERAGE: 'EGG_PROVIDER_OVERAGE',
  EMBYRO_CARRIER_OVERAGE: 'EMBYRO_CARRIER_OVERAGE',
} as const

export const IndividualRelationship = z.nativeEnum(IndividualRelationshipEnum)
export const IndividualSex = z.nativeEnum(IndividualSexEnum)
export const TreatmentProfileStatus = z.nativeEnum(TreatmentProfileStatusEnum)
export const PathwayPredictionStatus = z.nativeEnum(PathwayPredictionStatusEnum)
export const FertilityDiagnosis = z.nativeEnum(FertilityDiagnosisEnum)
export const UserResponse = z.nativeEnum(UserResponseEnum)
export const AttemptedConceptionPeriod = z.nativeEnum(
  AttemptedConceptionPeriodEnum
)
export const EmbryoTransferType = z.nativeEnum(EmbryoTransferTypeEnum)
export const IntendedStartPeriod = z.nativeEnum(IntendedStartPeriodEnum)
export const ConsultationType = z.nativeEnum(ConsultationTypeEnum)
export const NhsFundingEligibility = z.nativeEnum(NhsFundingEligibilityEnum)
export const IvfProcedure = z.nativeEnum(IvfProcedureEnum)
export const IvfUsProcedure = z.nativeEnum(IvfUsProcedureEnum)
export type IvfUsProcedure = z.infer<typeof IvfUsProcedure>

export const ExplicitIvfProcedure = z.nativeEnum(ExplicitIvfProcedureEnum)
export const FertilityProfileFlag = z.nativeEnum(FertilityProfileFlagEnum)

export type IndividualRelationship = z.infer<typeof IndividualRelationship>
export type IndividualSex = z.infer<typeof IndividualSex>
export type TreatmentProfileStatus = z.infer<typeof TreatmentProfileStatus>
export type PathwayPredictionStatus = z.infer<typeof PathwayPredictionStatus>
export type FertilityDiagnosis = z.infer<typeof FertilityDiagnosis>
export type UserResponse = z.infer<typeof UserResponse>
export type AttemptedConceptionPeriod = z.infer<
  typeof AttemptedConceptionPeriod
>
export type EmbryoTransferType = z.infer<typeof EmbryoTransferType>
export type IntendedStartPeriod = z.infer<typeof IntendedStartPeriod>
export type ConsultationType = z.infer<typeof ConsultationType>
export type NhsFundingEligibility = z.infer<typeof NhsFundingEligibility>
export type IvfProcedure = z.infer<typeof IvfProcedure>
export type ExplicitIvfProcedure = z.infer<typeof ExplicitIvfProcedure>
export type FertilityProfileFlag = z.infer<typeof FertilityProfileFlag>

export type TreatmentPrediction = {
  readonly modelID: string
  readonly modelType: string
  readonly cumulativeProbabilities: number[]
  readonly referenceValues: number[]
  readonly displayRoundIndex: number
  readonly status: PathwayPredictionStatus
  readonly error?: PredictionError[] | null
}

/**
 * An input type allowing users or ops to update the treatment profile
 * in its entirety
 */
export type TreatmentWholeInput = {
  readonly roleMember: PathwayIndividualInput
  readonly rolePartner: PathwayIndividualInput
  readonly roleEggProvider: PathwayIndividualInput
  readonly roleSpermProvider: PathwayIndividualInput
  readonly roleEmbryoCarrier: PathwayIndividualInput
  readonly diagnoses: readonly FertilityDiagnosis[]
  readonly diagnosesDescription?: null | string
  readonly explicitProcedures: readonly ExplicitIvfProcedure[]
  readonly haveAchievedLiveBirthPreviously: boolean
  readonly haveAchievedIvfLiveBirthPreviously?: null | boolean
  readonly previousIvf: boolean
  readonly previousIvfRounds?: null | number
  readonly previousIvfRoundResultingInLiveBirth?: null | number
  readonly lastFreshEggsCollected?: null | number
  readonly lastFreshEmbryosCreated?: null | number
  readonly lastEmbryosTransferred?: null | number
  readonly lastTransferType?: null | EmbryoTransferType
  readonly blastocystAge?: null | number
  readonly hasEmbryosStored?: null | boolean
  readonly embryosStored?: null | number
  readonly previousProcedures?: null | readonly IvfProcedure[]
  readonly eggsStored?: null | boolean
  readonly numEggsStored?: null | number
  readonly eggsStoredAge?: null | number
  readonly knowsAmh: boolean
  readonly intendedStartPeriod?: IntendedStartPeriod | null
  readonly previousConsultations: readonly ConsultationType[]
  readonly nhsFundingEligibility?: NhsFundingEligibility | null
  readonly extraInformationFromUser?: null | string
}

export const PathwayIndividualInputSchema = z.object({
  relationship: IndividualRelationship,
  sex: IndividualSex,
  dateOfBirth: Age18.nullish(),
  amh: AmhSchema.nullish(),
  weight: WeightSchema.nullish(),
  height: HeightSchema.nullish(),
})

export type PathwayIndividualInput = z.infer<
  typeof PathwayIndividualInputSchema
>

/**
 * Known individuals must be older than 18
 */
const Over18PathwayIndividualInputSchema = z.object({
  relationship: IndividualRelationship,
  sex: IndividualSex,
  dateOfBirth: Age18,
  amh: AmhSchema.nullish(),
  weight: WeightSchema.nullish(),
  height: HeightSchema.nullish(),
})

export type KnownPathwayIndividualInput = z.infer<
  typeof PathwayIndividualInputSchema
>

export const TreatmentWholeInputSchema = z.object({
  roleMember: PathwayIndividualInputSchema,
  rolePartner: PathwayIndividualInputSchema,
  roleEggProvider: PathwayIndividualInputSchema,
  roleSpermProvider: PathwayIndividualInputSchema,
  roleEmbryoCarrier: PathwayIndividualInputSchema,
  diagnoses: z.array(FertilityDiagnosis),
  diagnosesDescription: z.string().nullish(),
  explicitProcedures: z.array(ExplicitIvfProcedure),
  haveAchievedLiveBirthPreviously: z.boolean(),
  haveAchievedIvfLiveBirthPreviously: z.boolean().nullish(),
  previousIvf: z.boolean(),
  previousIvfRounds: NonNegativeIntSchema.nullish(),
  previousIvfRoundResultingInLiveBirth: NonNegativeIntSchema.nullish(),
  lastFreshEggsCollected: NonNegativeIntSchema.nullish(),
  lastFreshEmbryosCreated: NonNegativeIntSchema.nullish(),
  lastEmbryosTransferred: NonNegativeIntSchema.nullish(),
  lastTransferType: EmbryoTransferType.nullish(),
  blastocystAge: NonNegativeIntSchema.nullish(),
  hasEmbryosStored: z.boolean().nullish(),
  embryosStored: NonNegativeIntSchema.nullish(),
  previousProcedures: z.array(IvfProcedure).nullish(),
  eggsStored: z.boolean().nullish(),
  numEggsStored: NonNegativeIntSchema.nullish(),
  eggsStoredAge: NonNegativeIntSchema.nullish(),
  knowsAmh: z.boolean(),
  intendedStartPeriod: IntendedStartPeriod.nullish(),
  previousConsultations: z.array(ConsultationType),
  extraInformationFromUser: z.string().nullish(),
  previousPregnanciesCount: NonNegativeIntSchema,
  previousLiveBirthsCount: NonNegativeIntSchema,
})

const applySharedHealthSchemaRefinements = (schema: ZodSchema) =>
  schema

    // for diagnoses, if select dont know, cannot select any more
    .refine(
      (input) =>
        !input.diagnoses.includes(FertilityDiagnosisEnum.DontKnow) ||
        input.diagnoses.length === 1,
      {
        path: ['diagnoses'],
        message: `If you have a diagnosis, unselect "Don't' know"`,
      }
    )
    // for diagnoses, if select none, cannot select any more
    .refine(
      (input) =>
        !input.diagnoses.includes(FertilityDiagnosisEnum.None) ||
        input.diagnoses.length === 1,
      {
        path: ['diagnoses'],
        message: `If you have a diagnosis, unselect "None"`,
      }
    )
    // a custom text input is required if they select "other diagnosis"
    .refine(
      (input) =>
        !input.diagnoses.includes(FertilityDiagnosisEnum.Other) ||
        input.diagnosesDescription,
      {
        path: ['diagnosesDescription'],
        message: 'We need a little bit of context to help',
      }
    )
    // nullify diagnoses description if have not selected other
    .transform((input) => {
      if (input.diagnoses.includes(FertilityDiagnosis.enum.Other)) return input
      return {
        ...input,
        diagnosesDescription: null,
      }
    })
    // amh level required if they claim they know it
    .refine(
      (input) => {
        const value = input.roleEggProvider.amh?.value
        return input.knowsAmh === false || (isNumber(value) && value > 0)
      },
      {
        path: ['roleEggProvider.amh'],
        message: 'Required',
      }
    )
    // nullify amh if they claim they don't know it
    .transform((input) => {
      if (input.knowsAmh) return input
      return {
        ...input,
        roleEggProvider: { ...input.roleEggProvider, amh: null },
      }
    })

const KnownPathwayIndividual = Over18PathwayIndividualInputSchema
  // if female require height
  .refine(
    (individual) =>
      individual.sex !== IndividualSexEnum.Female || individual.height?.value,
    {
      path: ['height'],
      message: 'Height is required',
    }
  )
  // if female require weight
  .refine(
    (individual) =>
      individual.sex !== IndividualSexEnum.Female || individual.weight?.value,
    {
      path: ['weight'],
      message: 'Weight is required',
    }
  )
  // if female require BMI within range
  .refine(
    (individual) =>
      individual.sex !== IndividualSexEnum.Female ||
      isBmiWithinRange(individual.height, individual.weight),
    {
      path: ['weight'],
      message: 'Please double check the height and weight entered',
    }
  )

const sharedHealthSchemaExtension = {
  roleMember: KnownPathwayIndividual,
  rolePartner: KnownPathwayIndividual.or(
    PathwayIndividualInputSchema.refine(
      (partner) => partner.sex === IndividualSexEnum.Unknown,
      {
        path: ['sex'],
        message:
          'If you have a partner then you must complete all their details below',
      }
    )
  ).transform((partner) => {
    // nullify the partner fields if the user doesnt have one
    if (partner.sex !== IndividualSexEnum.Unknown) return partner
    return {
      sex: IndividualSexEnum.Unknown,
      relationship: IndividualRelationshipEnum.None,
      dateOfBirth: null,
      height: null,
      weight: null,
    }
  }),
  diagnoses: z.array(FertilityDiagnosis).nonempty({
    message:
      "Select a specific diagnosis, Unexplained Infertility, None or Don't know as appropriate",
  }),
}

const conditionalPregnancyExtension = {
  hasBeenPregnant: z.boolean(),
  previousPregnanciesCount: z.number().nullish(),
  previousLiveBirthsCount: z.number().nullish(),
}

const applyConditionalPregnancyRefinements = (schema: ZodSchema) =>
  schema
    .refine(
      (input) => {
        if (!input.hasBeenPregnant) return true
        return input.previousPregnanciesCount !== null
      },
      {
        path: ['previousPregnanciesCount'],
        message: 'Please input previous number of pregnancies',
      }
    )
    .refine(
      (input) => {
        if (!input.hasBeenPregnant) return true
        return input.previousLiveBirthsCount !== null
      },
      {
        path: ['previousLiveBirthsCount'],
        message: 'Please input previous number of births',
      }
    )
    .refine(
      (input) => {
        if (!input.hasBeenPregnant) return true

        const previousPregnanciesCount = input.previousPregnanciesCount ?? 0
        const previousLiveBirthsCount = input.previousLiveBirthsCount ?? 0

        return previousLiveBirthsCount <= previousPregnanciesCount
      },
      {
        path: ['previousLiveBirthsCount'],
        message:
          'Previous birth count cannot be larger than previous pregnancies.',
      }
    )
    .transform((input) => {
      if (input.hasBeenPregnant) return input
      return {
        ...input,
        previousPregnanciesCount: 0,
        previousLiveBirthsCount: 0,
      }
    })
    .transform((input) => {
      const haveAchievedLiveBirthPreviously = input?.previousLiveBirthsCount
        ? input?.previousLiveBirthsCount > 0
        : false
      return {
        ...input,
        haveAchievedLiveBirthPreviously,
      }
    })

export const TreatmentHealthInputSchema = applySharedHealthSchemaRefinements(
  applyConditionalPregnancyRefinements(
    TreatmentWholeInputSchema.pick({
      roleMember: true,
      rolePartner: true,
      knowsAmh: true,
      previousPregnanciesCount: true,
      previousLiveBirthsCount: true,
      diagnoses: true,
      diagnosesDescription: true,
    })
      .extend(sharedHealthSchemaExtension)
      .extend({
        roleEggProvider: PathwayIndividualInputSchema.extend({
          // nullable so its not required on health, but then subsequent pages can use the default schema
          relationship: z.nullable(IndividualRelationship),
        }),
      })
      .extend(conditionalPregnancyExtension)
  )
)

export type TreatmentHealthInput = z.infer<typeof TreatmentHealthInputSchema>

/**
 * Fields expected for pre-treatment model
 */
export const PreTreatmentFields = [
  'diagnoses', // from this we infer the status of all diagnosesDescription
  'procedures',
  'roleMember.dateOfBirth',
]

/**
 * Additional fields expected for post-treatment model
 */
export const PostTreatmentFields = [
  'previousIvf',
  'haveAchievedLiveBirthPreviously',
]

/**
 * Fields of a pathway which belong only to a single instance and so
 * would be ignored during a merge
 */
export const TransientPathwayFields = [
  'id',
  'prediction',
  'createdAt',
  'userId',
  'createdBy',
  'explicitProcedures',
]

/**
 * Fields which contain PathwayIndividual data to keep in sync
 */
export const PathwayRoleFields = [
  'roleMember',
  'rolePartner',
  'roleEmbryoCarrier',
  'roleSpermProvider',
  'roleEggProvider',
]

// schema refinements relating to future ivf fields
const applyFutureIvfSchemaRefinements = (schema: ZodSchema) =>
  schema
    // for previous consultations, if select none, cannot select any more
    .refine(
      (input) =>
        !input.previousConsultations.includes(ConsultationTypeEnum.None) ||
        input.previousConsultations.length === 1,
      {
        path: ['previousConsultations'],
        message: `If you have had a consultation, unselect "None"`,
      }
    )
    // for explicitProcedures, if select none, cannot select any more
    .refine(
      (input) =>
        !input.explicitProcedures.includes(ExplicitIvfProcedure.enum.None) ||
        input.explicitProcedures.length === 1,
      {
        path: ['explicitProcedures'],
        message: `If you require any IVF procedure, unselect "None"`,
      }
    )
    // require eggsStored if roleEggProvider is self or partner
    .refine(
      (input) =>
        ![
          IndividualRelationshipEnum.Self,
          IndividualRelationshipEnum.Partner,
        ].includes(input.roleEggProvider.relationship) ||
        input.eggsStored !== null,
      {
        path: ['eggsStored'],
        message: 'Required',
      }
    )
    // require number of stored eggs if claimed
    .refine(
      (input) =>
        !input.eggsStored || (input.numEggsStored && input.numEggsStored > 0),
      {
        path: ['numEggsStored'],
        message:
          "Specify how many eggs are in storage. If none, select 'No' above.",
      }
    )
    // require age of stored eggs if any
    .refine(
      (input) =>
        !input.eggsStored || (input.eggsStoredAge && input.eggsStoredAge >= 18),
      {
        path: ['eggsStoredAge'],
        message: 'Age when eggs were collected is required',
      }
    )
    // nullify the stored-eggs-related fields if the user has none
    .transform((input) => {
      if (input.eggsStored) return input
      return { ...input, numEggsStored: null, eggsStoredAge: null }
    })

const applyPreviousIvfSchemaRefinements = (schema: ZodSchema) =>
  schema
    // for previous procedures, if select none, cannot select any more
    .refine(
      (input) =>
        !input.previousProcedures.includes(IvfProcedure.enum.None) ||
        input.previousProcedures.length === 1,
      {
        path: ['previousProcedures'],
        message: `If you have had any previous IVF procedure, unselect "None"`,
      }
    )
    // if had ivf before, num of rounds required
    .refine((input) => !input.previousIvf || input.previousIvfRounds, {
      path: ['previousIvfRounds'],
      message: "Required. If you have not had IVF before, select 'No' above",
    })
    // if had ivf before, last fresh eggs required
    .refine(
      (input) => !input.previousIvf || isNumber(input.lastFreshEggsCollected),
      {
        path: ['lastFreshEggsCollected'],
        message: 'Required. If none were collected, specify zero',
      }
    )
    // lastTransferType required if we know they transferred some embryos
    .refine(
      (input) =>
        input.lastTransferType ||
        input.lastEmbryosTransferred === 0 ||
        input.lastEmbryosTransferred === null,
      { path: ['lastTransferType'], message: 'Required' }
    )
    // blastocystAge required if we know they transferred some embryos
    .refine(
      (input) =>
        input.blastocystAge ||
        input.lastEmbryosTransferred === 0 ||
        input.lastEmbryosTransferred === null,
      { path: ['blastocystAge'], message: 'Required' }
    )
    // if no embryos were transferred, by definition there is no blastocyst
    .transform((input) => {
      if (input.lastEmbryosTransferred && input.lastEmbryosTransferred > 0)
        return input
      return { ...input, blastocystAge: null }
    })
    // number of embryos created must be specified if eggs were collected
    .refine(
      (input) =>
        input.lastFreshEggsCollected === null ||
        input.lastFreshEggsCollected === 0 ||
        input.lastFreshEmbryosCreated !== null,
      { path: ['lastFreshEmbryosCreated'], message: 'Required' }
    )
    // number of embryos created must be less-than-or-equal-to eggs collected
    .refine(
      (input) =>
        !input.lastFreshEggsCollected ||
        !input.lastFreshEmbryosCreated ||
        input.lastFreshEmbryosCreated <= input.lastFreshEggsCollected,
      {
        path: ['lastFreshEmbryosCreated'],
        message: 'Must be less than, or equal to, number of eggs collected',
      }
    )
    // if no eggs were collected, by definition no embryos could have been created
    .transform((input) => {
      if (input.lastFreshEggsCollected && input.lastFreshEggsCollected > 0)
        return input
      return { ...input, lastFreshEmbryosCreated: null }
    })
    // number of embryos transferred must be specified if embryos were created
    .refine(
      (input) =>
        input.lastFreshEmbryosCreated === 0 ||
        input.lastFreshEmbryosCreated === null ||
        input.lastEmbryosTransferred !== null,
      { path: ['lastEmbryosTransferred'], message: 'Required number' }
    )
    // number of embryos transferred must be less-than-or-equal-to embryos created
    .refine(
      (input) =>
        !input.lastEmbryosTransferred ||
        !input.lastFreshEmbryosCreated ||
        input.lastEmbryosTransferred <= input.lastFreshEmbryosCreated,
      {
        path: ['lastEmbryosTransferred'],
        message: 'Must be less than or equal to number of embryos created',
      }
    )
    // number of stored embryos required if they say they have some
    .refine(
      (input) =>
        !input.hasEmbryosStored ||
        (isNumber(input.embryosStored) && input.embryosStored > 0),
      {
        path: ['embryosStored'],
        message: 'Required',
      }
    )
    // nullify embryos-stored if the user specifies they don't have any
    .transform((input) => {
      if (input.hasEmbryosStored) return input
      return { ...input, embryosStored: null }
    })
    .refine(
      (input) =>
        input.hasEmbryosStored !== null || !input.lastEmbryosTransferred,
      { path: ['hasEmbryosStored'], message: 'Required' }
    )
    // user must specify the round which resulted in a live birth is
    // required if they say they were successful before
    .refine(
      (input) =>
        !input.haveAchievedIvfLiveBirthPreviously ||
        (isNumber(input.previousIvfRoundResultingInLiveBirth) &&
          input.previousIvfRoundResultingInLiveBirth > 0),
      {
        path: ['previousIvfRoundResultingInLiveBirth'],
        message: 'Required. Round number cannot be zero',
      }
    )
    // user must specify the round which resulted in a live birth is
    // required if they say they were successful before
    .refine(
      (input) =>
        !input.previousIvfRoundResultingInLiveBirth ||
        input.previousIvfRoundResultingInLiveBirth <= input.previousIvfRounds,
      {
        path: ['previousIvfRoundResultingInLiveBirth'],
        message:
          "Cannot be larger than the number of rounds you've previously started",
      }
    )
    // nullify if the user has not had an ivf live-birth before
    .transform((input) => {
      if (input.haveAchievedIvfLiveBirthPreviously) return input
      return { ...input, previousIvfRoundResultingInLiveBirth: null }
    })
    // nullify all previous ivf fields if the user has not had previous ivf before
    .transform((input) => {
      if (!input.previousIvf) {
        return {
          ...input,
          ...defaultPreviousIvfFields(),
        }
      }
      return input
    })

const applyAllSchemaRefinements = (
  object: ZodType<unknown, ZodTypeDef, unknown>
) =>
  applyPreviousIvfSchemaRefinements(
    applyFutureIvfSchemaRefinements(applySharedHealthSchemaRefinements(object))
  )

const ExtendedTreatmentWholeInputSchema = TreatmentWholeInputSchema.extend(
  sharedHealthSchemaExtension
).extend({
  roleEggProvider: PathwayIndividualInputSchema,
  roleEmbryoCarrier: PathwayIndividualInputSchema,
  roleSpermProvider: PathwayIndividualInputSchema,
  previousConsultations: z.array(ConsultationType).nonempty({
    message:
      "Select the types of consultation you have already had or 'None' as appropriate",
  }),
})
// this schema applies everything applied to health plus those necessary from the journey screen
export const TreatmentWholeProfileInputSchema = applyAllSchemaRefinements(
  ExtendedTreatmentWholeInputSchema
)

export const UpdateTreatmentProfileInputSchema = applyAllSchemaRefinements(
  applyConditionalPregnancyRefinements(
    ExtendedTreatmentWholeInputSchema.extend(conditionalPregnancyExtension)
  )
)

export type TreatmentWholeProfileInput = z.infer<
  typeof TreatmentWholeProfileInputSchema
>

export type FertilityProfile = {
  id: string
  pathway: TreatmentPathwayWithExplicitProcedures
  status: TreatmentProfileStatus
  completed: boolean
}
